import type { Approver, FieldOption, FieldType, Presenter } from "@repo/types";
import { FormikSelectApprover } from "./FormikSelectApprover";
import { FormikTextArea } from "./FormikTextArea";
import { FormikDatePicker } from "./FormikDatePicker";
import { FormikCheckbox } from "./FormikCheckbox";
import type { CheckedState } from "@radix-ui/react-checkbox";
import { FormikSelect } from "./FormikSelect";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { FormikInput } from "./FormikInput";
import type { FormikErrors } from "formik";
import { FormikSelectPresenter } from "./FormikSelectPresenter";
import { FormikMultipleCheckboxes } from "./FormikMultipleCheckboxes";
import { FormikAutocomplete } from "./FormikAutocomplete";
import { Markdown } from "@repo/ui/components/Base/Markdown";

export interface DynamicFormikFieldProps {
  name: string;
  type: FieldType;
  value: unknown;
  values: {
    [x: string]: unknown;
  };
  label?: string;
  options?: FieldOption[];
  placeholder?: string;
  disabled?: boolean;
  errors?: FormikErrors<unknown>;
}

export const DynamicFormikField = ({
  name,
  type,
  value,
  values,
  errors,
  ...props
}: DynamicFormikFieldProps) => {
  const error = (errors as Record<string, string | undefined>)[name];

  switch (type) {
    case "approver":
      return (
        <FormikSelectApprover
          name={name}
          key={name}
          value={value as Approver | undefined}
          error={!!error}
          errorMessage={error}
          {...props}
        />
      );
    case "presenter": {
      return (
        <FormikSelectPresenter
          key={name}
          name={name}
          value={value as Presenter | undefined}
          team={values.teamId as string | undefined}
          label={props.label ?? ""}
          disabled={props.disabled}
          error={!!error}
          errorMessage={error}
        />
      );
    }
    case "text-area":
      return (
        <FormikTextArea
          id={name}
          name={name}
          key={name}
          value={(value as string | undefined) ?? ""}
          error={!!error}
          errorMessage={error}
          label={props.label ?? ""}
          {...props}
        />
      );
    case "date-picker":
      return (
        <FormikDatePicker
          id={name}
          name={name}
          key={name}
          label={props.label ?? ""}
          date={(value as Date | undefined) || undefined}
          error={!!error}
          errorMessage={error}
          {...props}
        />
      );
    case "checkbox":
      return (
        <FormikCheckbox
          name={name}
          key={name}
          label={props.label ?? ""}
          checked={value as CheckedState}
          orientation="horizontal"
          error={!!error}
          errorMessage={error}
          disabled={props.disabled}
        />
      );
    case "multiple-checkboxes":
      return (
        <FormikMultipleCheckboxes
          name={name}
          key={name}
          value={value as (string | number)[] | undefined}
          disabled={props.disabled}
          error={error}
          label={props.label ?? ""}
          options={props.options}
        />
      );
    case "select":
      return (
        <FormikSelect
          key={name}
          name={name}
          value={value as string}
          disabled={props.disabled}
          label={props.label ?? ""}
          error={!!error}
          errorMessage={error}
          options={
            props.options
              ? props.options.map((opt) => (
                  <SelectItem value={`${opt.value}`} key={`${opt.value}`}>
                    {opt.label}
                  </SelectItem>
                ))
              : []
          }
        />
      );
    case "optional-select":
      return (
        <FormikAutocomplete
          id={name}
          name={name}
          value={(value as string | undefined) ?? ""}
          disabled={props.disabled}
          label={props.label ?? ""}
          error={!!error}
          errorMessage={error}
          autocompleteOptions={
            props.options
              ? props.options.map((opt) => ({
                  text: "" + opt.value,
                  description: <Markdown label={opt.label} />,
                }))
              : []
          }
        />
      );
    default:
      return (
        <FormikInput
          id={name}
          key={name}
          name={name}
          placeholder={props.placeholder}
          type={type}
          value={
            (value as string | number | readonly string[] | undefined) ?? ""
          }
          error={!!error}
          errorMessage={error}
          {...props}
        />
      );
  }
};
